// @owners { team: patients }
import { ActionSheetContext, LoadingEmptyState } from '@alto/design-system';
import { SupportCaseContent, SupportCaseNotFound, useGetSupportCase, useGetSupportCaseFromCache } from '@alto/features';
import { useNavigation } from '@alto/navigation';
import React, { useCallback, useContext } from 'react';
import { useParams } from 'react-router-dom';
import { ASSISTANT_PRESSED_EVENTS } from '~shared/features/alto-assistant/analytics/constants';
import { ASSISTANT_CHAT_ORIGINS } from '~shared/features/alto-assistant/constants';
import { useAnalytics } from '~shared/hooks';
import { EVENTS } from '~shared/lib/analytics/src/constants';
import { Sentry } from '~shared/sentry';
import { HowCanWeHelpActionSheet } from '~web/features/alto-assistant/components/action-sheets/HowCanWeHelpActionSheet';

type SupportCaseRouteParams = {
  readonly supportCaseID: string;
};

/**
 * Component for single support case on the web, this is a wrapper for the @alto/features SupportCaseContent component
 * @returns SupportCase UI
 */
const SupportCase = () => {
  const { supportCaseID: supportCaseIDRouteParam } = useParams<SupportCaseRouteParams>();
  const { goBack, navigate } = useNavigation();
  const supportCaseID = Number(supportCaseIDRouteParam) || null;

  const { trackEvent } = useAnalytics();
  const { setActiveActionSheet } = useContext(ActionSheetContext);

  const { supportCaseCached } = useGetSupportCaseFromCache(supportCaseID);
  const {
    supportCaseNetwork,
    error: supportCaseError,
    isFetching: isFetchingSupportCase,
  } = useGetSupportCase(supportCaseID, !!supportCaseCached);

  const supportCase = supportCaseCached || supportCaseNetwork; // use cached data and fallback to network if needed (ie branch link click)

  const handleReplyPress = useCallback(() => {
    if (!supportCaseID) return;
    navigate('RouteAssistantSupportCaseReply', { supportCaseID });
    trackEvent({
      event: EVENTS.ASSISTANT_COMPONENT_PRESSED,
      params: { name: ASSISTANT_PRESSED_EVENTS.SUPPORT_CASE_REPLY_BUTTON, supportCaseID },
    });
  }, [navigate, supportCaseID, trackEvent]);

  if (isFetchingSupportCase && !supportCase) {
    return <LoadingEmptyState />;
  }

  if (!supportCase) {
    if (supportCaseError) {
      const context = {
        tags: { featureOwner: 'alto-assistant' },
        contexts: { 'Error Message Context': { error: supportCaseError } },
      };
      Sentry.captureMessage(`Error finding support case ${supportCaseID}`, context);
    }
    return <SupportCaseNotFound supportCaseID={supportCaseID} />;
  }

  const handleNewRequestPress = () => {
    trackEvent({
      event: EVENTS.ASSISTANT_COMPONENT_PRESSED,
      params: {
        name: ASSISTANT_PRESSED_EVENTS.SUPPORT_CASE_NEW_REQUEST,
        origin: ASSISTANT_CHAT_ORIGINS.CLOSED_SUPPORT_CASE,
        supportCaseID,
      },
    });
    setActiveActionSheet(<HowCanWeHelpActionSheet />);
  };

  return (
    <SupportCaseContent
      supportCase={supportCase}
      handleNewRequestPress={handleNewRequestPress}
      handleReplyPress={handleReplyPress}
      handleBackPress={goBack}
    />
  );
};

export default SupportCase;
